import { Individual } from '@semantical/modules/knowledge-graph';
import { RestrictedObjectProperty } from '@semantical/modules/ontology-model';
import { formatObjectPropertyCardinality } from './utils';

type Props = {
    restriction: RestrictedObjectProperty;
    individual: Individual;
    index: number;
    prefix: string;
    hasABoxDeclaration: boolean;
    onDeclare: (individual: Individual, restriction: RestrictedObjectProperty) => void;
    onClear: (individual: Individual, restriction: RestrictedObjectProperty) => void;
};

export default function HasValueChip({
    restriction,
    individual,
    index,
    prefix,
    hasABoxDeclaration,
    onDeclare,
    onClear
}: Props) {
    if (hasABoxDeclaration) {
        return (
            <div key={`${prefix}-${index}`} className="flex items-center gap-1.5 mb-1.5 py-0.5">
                <span className="text-[11px] text-slate-600 dark:text-white/75 min-w-20 flex-shrink-0">{restriction.label}:</span>
                <div
                    className="inline-flex items-center gap-1 px-2 py-[3px] bg-[rgba(100,180,255,0.1)] border border-[rgba(100,180,255,0.2)] rounded-xl text-[11px] text-[rgba(100,180,255,0.8)] flex-1 min-w-0"
                    title={`Declared on individual (value fixed by TBox): ${restriction.hasValueLabel!}`}
                >
                    <span className="text-[10px] opacity-80 flex-shrink-0">ℹ</span>
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap flex-1">{restriction.hasValueLabel!}</span>
                    <span className="text-[9px] opacity-50 flex-shrink-0">🔒</span>
                    <button
                        className="ml-1 px-0.5 text-[10px] bg-transparent border-none text-[rgba(255,100,100,0.7)] cursor-pointer leading-none"
                        tabIndex={-1}
                        onClick={() => onClear(individual, restriction)}
                        title="Clear ABox declaration (revert to TBox-only)"
                    >
                        ×
                    </button>
                </div>
                <span className="text-[9px] text-slate-400 dark:text-white/40 flex-shrink-0">
                    {formatObjectPropertyCardinality(restriction)}
                </span>
            </div>
        );
    }

    return (
        <div key={`${prefix}-${index}`} className="flex items-center gap-1.5 mb-1.5 py-0.5">
            <span className="text-[11px] text-slate-600 dark:text-white/75 min-w-20 flex-shrink-0">{restriction.label}:</span>
            <div
                className="inline-flex items-center gap-1 px-2 py-[3px] bg-[rgba(180,180,180,0.1)] border border-[rgba(180,180,180,0.2)] rounded-xl text-[11px] text-[rgba(180,180,180,0.8)] flex-1 min-w-0"
                title={`Inherited from class (TBox): ${restriction.hasValueLabel!}`}
            >
                <span className="text-[10px] opacity-70 flex-shrink-0">↓</span>
                <span className="overflow-hidden text-ellipsis whitespace-nowrap flex-1">{restriction.hasValueLabel!}</span>
                <span className="text-[9px] opacity-50 flex-shrink-0">🔒</span>
            </div>
            <button
                className="px-1.5 py-0.5 text-[10px] bg-accent/10 border border-accent/30 rounded text-accent/80 cursor-pointer flex-shrink-0"
                onClick={() => onDeclare(individual, restriction)}
                title="Declare this value on the individual (ABox assertion)"
            >
                +
            </button>
            <span className="text-[9px] text-slate-400 dark:text-white/40 flex-shrink-0">
                {formatObjectPropertyCardinality(restriction)}
            </span>
        </div>
    );
}
